import { z } from "zod";
import {
  ITEM_SCHEMAS,
  ITEM_TYPES,
  LcpManifestSchema,
  type ItemOf,
  type ItemType,
  type LcpManifest,
} from "./schemas";

/**
 * Raw, unvalidated content-pack input: an LCP manifest plus the per-type JSON
 * arrays keyed by item type. This is the shape an LCP ZIP or the
 * `@massif/lancer-data` adapter produces *before* validation.
 */
export type RawPack = {
  manifest: unknown;
  items: Partial<Record<ItemType, unknown>>;
}

/**
 * Build a {@link RawPack} from an **object-of-arrays** source (the shape every
 * `@massif/*-data` package exports: one object with `frames`/`weapons`/… array
 * fields). `dataKeys` maps each of our {@link ITEM_TYPES} to the source's field
 * name (or `null` when the source has no such type). One shared copy loop for
 * every such adapter.
 */
export function objectOfArraysToRawPack(
  data: Record<string, unknown>,
  dataKeys: Record<ItemType, string | null>,
  manifest: unknown,
): RawPack {
  const items: RawPack["items"] = {};
  for (const type of ITEM_TYPES) {
    const key = dataKeys[type];
    if (key === null) continue;
    items[type] = data[key];
  }
  return { manifest, items };
}

/** A fully validated content pack: every item parsed against its schema. */
export type Pack = {
  /** Stable pack identifier (manifest name + version), used for provenance. */
  id: string;
  manifest: LcpManifest;
  items: { [T in ItemType]: ItemOf<T>[] };
}

/** Thrown when a pack fails validation. Carries the underlying zod issues, unswallowed. */
export class PackValidationError extends Error {
  readonly issues: z.ZodIssue[];
  constructor(message: string, issues: z.ZodIssue[]) {
    super(message);
    this.name = "PackValidationError";
    this.issues = issues;
  }
}

function emptyItems(): { [T in ItemType]: ItemOf<T>[] } {
  const out = {} as { [T in ItemType]: ItemOf<T>[] };
  for (const t of ITEM_TYPES) out[t] = [] as never;
  return out;
}

/** Derive a deterministic pack id from its manifest (`<name>@<version>`). */
export function packId(manifest: LcpManifest): string {
  return `${manifest.name}@${manifest.version}`;
}

/**
 * Validate a {@link RawPack} into a {@link Pack}. Malformed input rejects with a
 * {@link PackValidationError} whose message includes the failing path and the
 * surfaced zod issues — never a swallowed/generic failure.
 */
export function parsePack(raw: RawPack): Pack {
  const manifestResult = LcpManifestSchema.safeParse(raw.manifest);
  if (!manifestResult.success) {
    throw new PackValidationError(
      `Invalid lcp_manifest.json: ${formatIssues(manifestResult.error.issues)}`,
      manifestResult.error.issues,
    );
  }
  const manifest = manifestResult.data;

  const items = emptyItems();
  for (const type of ITEM_TYPES) {
    const rawArray = raw.items[type];
    if (rawArray === undefined) continue; // a pack need not define every type
    if (!Array.isArray(rawArray)) {
      throw new PackValidationError(
        `Expected ${type} (${ITEM_SCHEMAS[type].file}) to be a JSON array, got ${typeof rawArray}`,
        [],
      );
    }
    const arraySchema = z.array(ITEM_SCHEMAS[type].schema);
    const result = arraySchema.safeParse(rawArray);
    if (!result.success) {
      throw new PackValidationError(
        `Invalid ${type} in pack "${manifest.name}" (${ITEM_SCHEMAS[type].file}): ${formatIssues(result.error.issues)}`,
        result.error.issues,
      );
    }
    // result.data is ItemOf<type>[]; the per-type assignment is sound by construction.
    items[type] = result.data as never;
  }

  return { id: packId(manifest), manifest, items };
}

function formatIssues(issues: z.ZodIssue[]): string {
  return issues
    .slice(0, 5)
    .map((i) => `[${i.path.join(".") || "<root>"}] ${i.message}`)
    .join("; ");
}
